import { Directive, ElementRef, OnInit, OnDestroy, NgZone } from '@angular/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

@Directive({
  selector: '[appStackScroll]'
})
export class StackScrollDirective implements OnInit, OnDestroy {
  private animation: gsap.core.Timeline | null = null;
  private scrollTrigger?: ScrollTrigger;
  private gsapCtx: any;
  private zIndex = 10; // Base z-index

  constructor(
    private el: ElementRef,
    private zone: NgZone
  ) {
    gsap.registerPlugin(ScrollTrigger);
  }

  ngOnInit() {
    this.zone.runOutsideAngular(() => {
      try {
        this.gsapCtx = gsap.context(() => {
          // Set initial state - below viewport
          gsap.set(this.el.nativeElement, {
            y: '100vh',
            opacity: 0,
            zIndex: this.zIndex,
            position: 'relative'
          });

          // Create stacking animation timeline
          this.animation = gsap.timeline({
            scrollTrigger: {
              trigger: this.el.nativeElement,
              start: "top bottom-=10%",
              end: "top top",
              scrub: 0.8,
              markers: false,
              onEnter: () => this.bringToFront(),
              onLeaveBack: () => this.sendToBack(),
              onToggle: self => this.handleToggle(self)
            }
          });

          // Animation sequence
          this.animation
            .to(this.el.nativeElement, {
              y: '0vh',
              opacity: 1,
              duration: 1,
              ease: "power3.inOut"
            }, 0)
            .fromTo(this.el.nativeElement,
              { scale: 0.98 },
              { scale: 1, duration: 0.5, ease: "back.out(1.2)" },
            0.3
            );

          this.scrollTrigger = this.animation?.scrollTrigger;
        }, this.el.nativeElement);
      } catch (e) {
        // fallback: minimal setup
        gsap.set(this.el.nativeElement, { y: '100vh', opacity: 0, zIndex: this.zIndex, position: 'relative' });
        this.animation = gsap.timeline();
      }
    });
  }

  private bringToFront() {
    // When entering viewport
    gsap.to(this.el.nativeElement, {
      zIndex: this.zIndex + 10,
      duration: 0.3
    });
  }

  private sendToBack() {
    // When leaving viewport (scrolling up)
    gsap.to(this.el.nativeElement, {
      zIndex: this.zIndex,
      duration: 0.3
    });
  }

  private handleToggle(trigger: ScrollTrigger) {
    if (trigger.isActive) {
      this.animation.play();
    } else {
      this.animation.reverse();
    }
  }

  ngOnDestroy() {
    this.zone.runOutsideAngular(() => {
      try {
        if (this.scrollTrigger) try { this.scrollTrigger.kill(); } catch (e) {}
      } catch (e) {}
      try { if (this.animation) this.animation.kill(); } catch (e) {}
      try { if (this.gsapCtx && typeof this.gsapCtx.revert === 'function') this.gsapCtx.revert(); } catch (e) {}

      // Reset styles defensively
      try {
        gsap.set(this.el.nativeElement, {
          y: '',
          opacity: '',
          zIndex: '',
          position: '',
          scale: ''
        });
      } catch (e) {}
    });
  }
}